/**
 * @file 教师管理主面板
 * @description RequestTable + 本地 mock；科目、学员人数弹窗、行操作二次确认。
 *
 * @whenToUse 仅由 TeacherPage 组合。
 * @whenNotToUse 其他业务列表页。
 */

import { useState } from 'react'
import { MoreHorizontal, Plus } from 'lucide-react'
import { confirm } from '@/components/ui/confirm.tsx'
import { Badge } from '@/components/ui/badge.tsx'
import { Button } from '@/components/ui/button.tsx'
import { type DataTableColumn } from '@/components/ui/data-table.tsx'
import { DropdownMenu } from '@/components/ui/dropdown-menu.tsx'
import { IconButton } from '@/components/ui/icon-button.tsx'
import { PageHeader } from '@/components/ui/page-header.tsx'
import { RequestTable } from '@/components/ui/request-table.tsx'
import { toast } from '@/components/ui/toast.tsx'
import { TeacherFormDialog } from '@/components/pages/teacher-page/teacher-form-dialog.tsx'
import { TeacherSearchFields } from '@/components/pages/teacher-page/teacher-search-fields.tsx'
import { TeacherStudentsDialog } from '@/components/pages/teacher-page/teacher-students-dialog.tsx'
import {
  EMPTY_TEACHER_QUERY,
  MOCK_TEACHERS,
  filterTeacherRows,
  formatSubjects,
  type TeacherFormValues,
  type TeacherQuery,
  type TeacherRow,
} from '@/components/pages/teacher-page/mock.ts'

/**
 * 格式化本地时间字符串
 * @param date 时间
 */
function formatDateTime(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
}

/**
 * 教师管理面板
 */
export function TeacherPanel() {
  const [rows, setRows] = useState<TeacherRow[]>(MOCK_TEACHERS)
  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<TeacherRow | null>(null)
  const [studentsOpen, setStudentsOpen] = useState(false)
  const [studentsTarget, setStudentsTarget] = useState<TeacherRow | null>(null)

  const openCreate = () => {
    setEditing(null)
    setFormOpen(true)
  }

  const openEdit = (row: TeacherRow) => {
    setEditing(row)
    setFormOpen(true)
  }

  /**
   * 打开学员列表弹窗；人数为 0 时仅轻提示
   * @param row 教师行
   */
  const openStudentsDialog = (row: TeacherRow) => {
    if (row.studentCount <= 0) {
      toast.show(`「${row.name}」当前没有学员`)
      return
    }
    setStudentsTarget(row)
    setStudentsOpen(true)
  }

  const handleFormSubmit = (values: TeacherFormValues) => {
    if (editing) {
      setRows((prev) =>
        prev.map((row) =>
          row.id === editing.id
            ? {
                ...row,
                name: values.name.trim(),
                account: values.account.trim(),
                subjects: values.subjects,
                enabled: values.enabled,
              }
            : row,
        ),
      )
      toast.success('已保存教师')
      return
    }

    const id = `t-${Date.now()}`
    setRows((prev) => [
      {
        id,
        name: values.name.trim(),
        account: values.account.trim(),
        subjects: values.subjects,
        studentCount: 0,
        createdAt: formatDateTime(new Date()),
        enabled: values.enabled,
      },
      ...prev,
    ])
    toast.success(
      '已新增教师',
      values.password.trim() ? undefined : '密码默认 123456',
    )
  }

  /**
   * 重置密码（二次确认）
   * @param row 教师行
   */
  const handleResetPassword = async (row: TeacherRow) => {
    const ok = await confirm({
      title: '重置密码？',
      description: `将「${row.name}」账号 ${row.account} 的密码重置为 123456，确认是否重置？`,
      confirmText: '重置',
    })
    if (!ok) return
    toast.success('密码已重置为 123456', row.name)
  }

  /**
   * 启用 / 禁用（二次确认）
   * @param row 教师行
   */
  const handleToggleEnabled = async (row: TeacherRow) => {
    const willEnable = !row.enabled
    const ok = await confirm({
      title: willEnable ? '启用教师？' : '禁用教师？',
      description: willEnable
        ? `确认启用「${row.name}」？启用后可正常登录使用。`
        : `确认禁用「${row.name}」？禁用后将无法登录。`,
      confirmText: willEnable ? '启用' : '禁用',
      danger: !willEnable,
    })
    if (!ok) return
    setRows((prev) =>
      prev.map((item) =>
        item.id === row.id ? { ...item, enabled: willEnable } : item,
      ),
    )
    toast.success(willEnable ? '已启用' : '已禁用', row.name)
  }

  /**
   * 删除教师（二次确认）
   * @param row 教师行
   */
  const handleDelete = async (row: TeacherRow) => {
    const ok = await confirm({
      title: '删除教师？',
      description: `将删除「${row.name}」，此操作不可恢复。`,
      confirmText: '删除',
      danger: true,
    })
    if (!ok) return
    setRows((prev) => prev.filter((item) => item.id !== row.id))
    toast.success('已删除', row.name)
  }

  const columns: DataTableColumn<TeacherRow>[] = [
    { key: 'name', title: '教师名称', dataIndex: 'name', minWidth: 100 },
    { key: 'account', title: '教师账号', dataIndex: 'account', minWidth: 120 },
    {
      key: 'subjects',
      title: '科目',
      minWidth: 140,
      render: (row) => formatSubjects(row.subjects),
    },
    {
      key: 'enabled',
      title: '教师状态',
      minWidth: 88,
      render: (row) => (
        <Badge variant={row.enabled ? 'success' : 'danger'}>
          {row.enabled ? '启用' : '禁用'}
        </Badge>
      ),
    },
    {
      key: 'studentCount',
      title: '学员人数',
      minWidth: 96,
      render: (row) => (
        <button
          type="button"
          className="text-primary transition-opacity duration-150 hover:opacity-90"
          onClick={() => openStudentsDialog(row)}
        >
          {row.studentCount} 人
        </button>
      ),
    },
    {
      key: 'createdAt',
      title: '创建时间',
      dataIndex: 'createdAt',
      minWidth: 168,
    },
    {
      key: 'actions',
      title: '操作',
      fixed: 'right',
      width: 72,
      render: (row) => (
        <DropdownMenu
          align="end"
          trigger={
            <IconButton
              variant="ghost"
              aria-label={`${row.name} 操作`}
              icon={<MoreHorizontal className="size-4" aria-hidden />}
            />
          }
          items={[
            {
              key: 'reset',
              label: '重置密码',
              onSelect: () => {
                void handleResetPassword(row)
              },
            },
            {
              key: 'toggle',
              label: row.enabled ? '禁用' : '启用',
              danger: row.enabled,
              onSelect: () => {
                void handleToggleEnabled(row)
              },
            },
            {
              key: 'edit',
              label: '编辑',
              onSelect: () => openEdit(row),
            },
            { key: 'div-1', type: 'divider' },
            {
              key: 'delete',
              label: '删除',
              danger: true,
              onSelect: () => {
                void handleDelete(row)
              },
            },
          ]}
        />
      ),
    },
  ]

  return (
    <>
      <PageHeader
        title="教师管理"
        description="管理教师档案、授课科目与启用状态。"
        extra={
          <Button
            icon={<Plus className="size-4" aria-hidden />}
            onClick={openCreate}
          >
            新增教师
          </Button>
        }
      />

      <div className="mt-6">
        <RequestTable<TeacherRow, TeacherQuery>
          data={rows}
          filterData={filterTeacherRows}
          pageSize={10}
          columns={columns}
          searchForm={{
            initialValues: EMPTY_TEACHER_QUERY,
            items: TeacherSearchFields,
          }}
        />
      </div>

      <TeacherFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        editing={editing}
        onSubmit={handleFormSubmit}
      />

      <TeacherStudentsDialog
        open={studentsOpen}
        onOpenChange={setStudentsOpen}
        teacher={studentsTarget}
      />
    </>
  )
}
